import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ActiveCallDetail from "./components/ActiveCallDetail";
import Button from "./components/base/Button";
import Vapi from "@vapi-ai/web";
import { isPublicKeyMissingError } from "./utils";

const vapi = new Vapi("beb73690-7a66-4e4d-99ce-0a05deab797d");

const App = () => {
  const { isAuthenticated, isLoading, loginWithRedirect, logout, user } = useAuth0();
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [assistantIsSpeaking, setAssistantIsSpeaking] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [showPublicKeyInvalidMessage, setShowPublicKeyInvalidMessage] = useState(false);
  const [partialTranscript, setPartialTranscript] = useState("");
  const [finalTranscript, setFinalTranscript] = useState("");

  const userName = useMemo(() => {
    return user?.given_name || user?.name?.split(' ')[0] || user?.nickname?.split(' ')[0] || 'User';
  }, [user]);

  const assistantOptions = useMemo(() => ({
    name: "Life Share",
    firstMessage: `Welcome to Life Share, ${userName}! How can I assist you today?`,
    transcriber: {
      provider: "deepgram",
      model: "nova-2",
    },
    voice: {
      provider: "playht",
      voiceId: "jennifer",
    },
    model: {
      provider: "openai",
      model: "gpt-3.5-turbo",
      messages: [
        {
          role: "system",
          content: "You are a memory assistant. Your primary functions are to save and recall memories for the user. Always confirm when a memory is saved and provide the recalled memory when asked.",
        }
      ],
      functions: [
        {
          name: "saveMemory",
          description: "Used to save the memory to the vector database",
          parameters: {
            type: "object",
            properties: {
              memory: {
                type: "string",
                description: "The memory to be saved by the assistant",
              },
            },
            required: ["memory"],
          },
          serverUrl: "https://time-machine-rho.vercel.app/api/function-call",
        },
        {
          name: "recallMemory",
          description: "Recalls memories about the user from the vector database.",
          parameters: {
            type: "object",
            properties: {
              memory: {
                type: "string",
                description: "The memory to be recalled to the assistant.",
              },
            },
            required: ["memory"],
          },
          serverUrl: "https://time-machine-rho.vercel.app/api/function-call",
        },
      ],
    },
  }), [userName]);

  const handleVapiMessage = useCallback(async (msg) => {
    console.log("Received message:", msg);

    if (msg.type === "transcript") {
      if (msg.transcriptType === "partial") {
        setPartialTranscript(msg.transcript);
      } else if (msg.transcriptType === "final") {
        setFinalTranscript(msg.transcript);
        setPartialTranscript(""); // Clear partial transcript when final is received
      }
    }
    // Remove the function-call handling as it's now managed by Vapi
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      // Remove existing listeners
      vapi.removeAllListeners();

      // Set up new listeners
      vapi.on("call-start", () => {
        setConnecting(false);
        setConnected(true);
        setShowPublicKeyInvalidMessage(false);
      });

      vapi.on("call-end", () => {
        setConnecting(false);
        setConnected(false);
        setShowPublicKeyInvalidMessage(false);
      });

      vapi.on("speech-start", () => setAssistantIsSpeaking(true));
      vapi.on("speech-end", () => setAssistantIsSpeaking(false));
      vapi.on("volume-level", (level) => setVolumeLevel(level));

      vapi.on("error", (error) => {
        console.error('Vapi error:', error);
        setConnecting(false);
        if (isPublicKeyMissingError({ vapiError: error })) {
          setShowPublicKeyInvalidMessage(true);
        }
      });

      vapi.on("message", handleVapiMessage);

      return () => {
        vapi.removeAllListeners();
      };
    }
  }, [isAuthenticated, user, handleVapiMessage]);

  const startCallInline = async () => {
    if (isAuthenticated && user) {
      setConnecting(true);
      try {
        console.log('Starting Vapi call with options:', JSON.stringify(assistantOptions, null, 2));
        await vapi.start(assistantOptions);
        console.log('Vapi call started successfully');
      } catch (error) {
        console.error('Error starting Vapi call:', error);
        
        // Log the entire error object
        console.error('Full error object:', JSON.stringify(error, null, 2));
        
        if (error.error && typeof error.error === 'object') {
          console.error('Error details:', error.error);
        }
        
        if (error.body && typeof error.body === 'object') {
          console.error('Error body:', error.body);
        } else if (error.body && typeof error.body === 'string') {
          console.error('Error body:', error.body);
        }
        
        setConnecting(false);
      }
    } else {
      console.error('User is not authenticated');
      loginWithRedirect();
    }
  };

  const endCall = () => {
    vapi.stop();
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh' }}>
      {isAuthenticated && (
        <button
          onClick={() => logout({ returnTo: window.location.origin })}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'none',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontSize: '16px',
          }}
        >
          Log Out
        </button>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {!isAuthenticated ? (
          <div>
            <h1>Welcome to Life Share</h1>
            <p>Please log in to access the application</p>
            <Button label="Log In" onClick={() => loginWithRedirect()} />
          </div>
        ) : (
          <>
            <h1 style={{ color: 'white' }}>Welcome, {userName}!</h1>
            {!connected ? (
              <Button
                label="Begin"
                onClick={startCallInline}
                isLoading={connecting}
              />
            ) : (
              <ActiveCallDetail
                assistantIsSpeaking={assistantIsSpeaking}
                volumeLevel={volumeLevel}
                onEndCallClick={endCall}
              />
            )}
          </>
        )}

        {showPublicKeyInvalidMessage && <PleaseSetYourPublicKeyMessage />}
      </div>

      {connected && (
        <div>
          <p>Partial Transcript: {partialTranscript}</p>
          <p>Final Transcript: {finalTranscript}</p>
        </div>
      )}
    </div>
  );
};

const PleaseSetYourPublicKeyMessage = () => {
  return (
    <div
      style={{
        position: "fixed",
        bottom: "25px",
        left: "25px",
        padding: "10px",
        color: "#fff",
        backgroundColor: "#f03e3e",
        borderRadius: "5px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
      }}
    >
      Is your Vapi Public Key missing? (recheck your code)
    </div>
  );
};

export default App;